import React, { useState, useContext, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtModalError from "../../components/NtModalError";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import { fetchDialplan, updateDialplan } from "../../api/dialplan";

// Validate via the browser's built-in DOMParser. Wrap in a fake root so
// snippets with multiple top-level elements still parse.
function validateXml(xml) {
  if (!xml?.trim()) return "XML is empty";
  const wrapped = `<__root__>${xml}</__root__>`;
  const doc = new DOMParser().parseFromString(wrapped, "application/xml");
  const err = doc.querySelector("parsererror");
  if (!err) return null;
  const text = err.textContent || "";
  const match = text.match(/error on line \d+[^\n]*/i);
  return match ? match[0].trim() : "Invalid XML";
}

function NtDialplanXmlEditor({ item, onCancel, onSave }) {
  const context = useContext(UserContext);
  const { theme } = useTheme();
  const [xml, setXml] = useState(item?.dialplan_xml || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refresh on open — list responses can omit/stale this field; the single-get
  // endpoint is authoritative.
  useEffect(() => {
    let alive = true;
    (async () => {
      const domain = context.user?.domain;
      const id = item?.dialplan_uuid;
      if (!domain || !id) return;
      const res = await fetchDialplan(domain, id);
      Logger("Dialplan fetch (xml editor)", res);
      if (alive && res?.ok && res.data?.dialplan_xml != null) {
        setXml(res.data.dialplan_xml || "");
      }
    })();
    return () => {
      alive = false;
    };
  }, [item?.dialplan_uuid, context.user?.domain]);

  const handleSave = async () => {
    const validationError = validateXml(xml);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    const response = await updateDialplan(
      context.user?.domain,
      item.dialplan_uuid,
      { dialplan_xml: xml },
    );

    Logger("Dialplan XML Update", response);

    if (response.ok) {
      onSave?.(response.data);
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Edit XML</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Edit the dialplan XML directly. Save will run a syntax check first.
      </NtModalSubtitle>

      <NtModalInputTitle style={styles.inputTitle}>Dialplan XML</NtModalInputTitle>
      <textarea
        style={{
          ...styles.xmlInput,
          backgroundColor: theme.surface2,
          borderColor: theme.borderSoft,
          color: theme.text,
        }}
        placeholder={"<extension name=...>"}
        value={xml}
        onChange={(e) => setXml(e.target.value)}
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
      />

      <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

      <View style={styles.footer}>
        <View />
        <View style={styles.footerRight}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}
          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={"Save"}
            onPress={handleSave}
            enabled={!!xml}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 800,
    height: 600,
    minWidth: 500,
    minHeight: 400,
    padding: 15,
    resize: "both",
    overflow: "hidden",
  },
  inputTitle: {
    marginTop: 20,
    marginLeft: 4,
    marginBottom: 4,
  },
  xmlInput: {
    flex: 1,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 6,
    padding: "10px 12px",
    fontFamily: "monospace",
    fontSize: 13,
    outline: "none",
    resize: "none",
    boxSizing: "border-box",
    minHeight: 0,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 15,
  },
  footerRight: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default NtDialplanXmlEditor;
